<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="jps-item-selector"
      v-if="innerSs"
      :no-footer="!_multiple"
      dialog-width="100vw"
      dialog-height="100vh"
    >
      <template #header>
        <BaseIcon name="mdi/selection" />
        {{ ls({ ja: 'メタデータの選択', en: 'Select Metadata' }) }}
      </template>
      <div class="jps-modal-content">
        <p class="jps-message" v-if="_message">{{ ls(_message) }}</p>
        <BaseTab :tab-options="headers" v-model="tabIndex">
          <BaseTabPanel val="search">
            <SearchBox :ss="innerSs" :additional="true" />
            <div class="result" v-if="innerSs.searched">
              <SearchCount :ss="innerSs" />
              <div class="panel-result">
                <div class="search-result-panel">
                  <UiDataPanel
                    v-for="item in innerSs.state.result.list"
                    :key="item.id"
                    type="item"
                    :data="item"
                    :nolink="true"
                    :selectable="true"
                    :tablink="true"
                    @click.native="selectItem(item)"
                  />
                  <SearchInfiniteLoading :ss="innerSs" />
                </div>
              </div>
            </div>
          </BaseTabPanel>
          <BaseTabPanel val="map">
            <SearchBox :ss="innerSsMap" />
            <UiItemSearchResultMap :ss="innerSsMap" />
          </BaseTabPanel>
        </BaseTab>
      </div>
      <template #footer-extra>
        <div class="modal-footer-selected">
          <UiItemSpan
            class="jps-tag jps-dark jps-tag-ricon"
            v-for="itemId in selectedValue"
            :key="itemId"
            :id="itemId"
          >
            <template v-slot:default="{ item }">
              {{ li(item) }}
              <BaseIcon
                class="jps-icon-button is-size-6"
                name="mdi/close-circle"
                @click="removeFromArray(selectedValue, item.id)"/>
            </template>
          </UiItemSpan>
        </div>
      </template>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'
import type { TabOption } from '~/components/base/BaseTab.vue'

const { ls, li } = useI18n()
const dataStore = useDataStore()
const { getSearchId } = useItem()

interface Props {
  multiple?: boolean
  message?: LangString
  selected?: string[]
  ss?: SearchStore<Item>
  map?: boolean
}
const props = withDefaults(defineProps<Props>(), {
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _multiple: Ref<boolean> = ref()
const _message: Ref<LangString> = ref()
const _selected: Ref<string[]> = ref()
const _ss: Ref<SearchStore<Item>> = ref()
const _map: Ref<boolean> = ref()
const innerSs: Ref<SearchStore<Item>> = ref(null)
const innerSsMap: Ref<SearchStore<Item>> = ref(null)
const selectedValue = ref<string[]>()
const openAwait = async (options?: Props) => {
  selectedValue.value = []

  setOptionsValue(_multiple, 'multiple', props, options, false)
  setOptionsValue(_message, 'message', props, options)
  setOptionsValue(_selected, 'selected', props, options, false)
  setOptionsValue(_ss, 'ss', props, options)
  setOptionsValue(_map, 'map', props, options, false)

  if (_selected.value) selectedValue.value = clone(props.selected)
  if (_ss.value) {
    innerSs.value = _ss.value
    innerSsMap.value =  useSearch<Item>(
      _ss.value.state.searchdef,
      _ss.value.state.searchService,
      { configSyncRoute: false }
    )
  } else {
    innerSs.value = useSearch<Item>(systemCrossSearchCs(), getSearchId(systemCrossSearchId()), { configSyncRoute: false })
    innerSsMap.value = useSearch<Item>(systemCrossSearchCs(), getSearchId(systemCrossSearchId()), { configSyncRoute: false })
  }
  innerSs.value.config.configFreeze = false
  nextTick(() => {
    innerSs.value.requestKeywordFocus()
  })

  return await modal.value.openAwait<Item | string[]>()
}
defineExpose({ openAwait })

const headers = computed<TabOption[]>(() => {
  const h = [
    {
      val: 'search',
      label: ls({ ja: '検索結果から選択', en: 'By Search' }),
    },
  ]
  if (_map.value) {
    h.push({
      val: 'map',
      label: ls({ ja: '地図から選択', en: 'By Map' }),
    })
  }
  return h
})
const tabIndex = ref(headers.value[0].val)

const selectItem = (i: any) => {
  if (_multiple.value) {
    if (!selectedValue.value.includes(i.id)) {
      dataStore.put('item', i.id, i)
      selectedValue.value.push(i.id)
    } else {
      removeFromArray(selectedValue.value, i.id)
    }
  } else {
    modal.value.resolve(i)
  }
}

const confirm = () => {
  modal.value.resolve(selectedValue.value)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-item-selector {
  .jps-search-box {
    margin-top: 0.5rem;
  }

  .jps-data-panel {
    margin-bottom: 0.5rem;
    background-color: white;
  }

  .panel-result {
    display: flex;
    flex-direction: column;

    .facet-toggle {
      display: block;
      text-align: right;
      margin-bottom: 1em;
      @include is-tablet {
        display: none;
      }
    }

    .facets {
      display: none;
      width: 17rem;
      margin-right: 2em;
      margin-bottom: 2em;
      @include is-tablet {
        display: block;
      }
      .search-facet {
        margin-top: 1em;
      }
    }

    .search-result-panel {
      flex-grow: 1;
    }
    @include is-tablet {
      flex-direction: row;
    }
  }

  .modal-footer-selected {
    padding: 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;

    .jps-tag {
      margin: 0.125rem 0;
    }

    max-height: 116px;
    overflow-y: auto;
  }
}
</style>
